/**
 * Public model of a brand (ADR-0045): Identity + Theme + Design configuration, authored as data in
 * `brands/<id>/` and compiled into a deterministic artifact.
 */

/** Brand lifecycle status (ADR-0045 D11). Only `published` and `deprecated` build for production. */
export enum BrandStatus {
	Draft = 'draft',
	ReadyForReview = 'ready-for-review',
	Approved = 'approved',
	Published = 'published',
	Deprecated = 'deprecated',
}

/** Platform role of a brand (TO-517). Exactly one brand is the `reference`; an absent role is `standard`. */
export enum BrandRole {
	Reference = 'reference',
	Standard = 'standard',
}

/** Build mode: `production` enforces the lifecycle gate; `development` compiles every status. */
export enum BrandBuildMode {
	Production = 'production',
	Development = 'development',
}

/** Where an identity font comes from (ADR-0045 D9). */
export enum FontSource {
	SelfHosted = 'self-hosted',
	System = 'system',
}

/** Font style of an identity font. */
export enum FontStyle {
	Normal = 'normal',
	Italic = 'italic',
}

/** The DTCG `$type` values a brand theme may use (ADR-0045 D2 subset). */
export enum TokenType {
	Color = 'color',
	Dimension = 'dimension',
	FontFamily = 'fontFamily',
	FontWeight = 'fontWeight',
	Number = 'number',
	Duration = 'duration',
	CubicBezier = 'cubicBezier',
	Shadow = 'shadow',
}

/** The role an asset file plays in a brand. */
export enum AssetRole {
	Logo = 'logo',
	LogoInverse = 'logoInverse',
	Symbol = 'symbol',
	Favicon = 'favicon',
	Font = 'font',
}

/** A reference to a brand asset file, relative to `brands/<id>/`. */
export interface BrandAssetRef {
	src: string
	alt?: string
}

/** One self-hosted font file: a single weight × style combination. */
export interface BrandFontFile {
	src: string
	weight: number
	style: FontStyle
}

/** An identity font. Self-hosted fonts carry licence metadata and one file per weight × style. */
export interface BrandFontRef {
	family: string
	source: FontSource
	weights: number[]
	styles?: FontStyle[]
	license?: string
	licenseUrl?: string
	selfHostingPermitted?: true
	files?: BrandFontFile[]
}

/** Who the brand is: name, marks and fonts. Kept separate from theme tokens (R-BRAND-001). */
export interface BrandIdentity {
	name: string
	assets: {
		logo: BrandAssetRef
		logoInverse?: BrandAssetRef
		symbol?: BrandAssetRef
		favicon?: BrandAssetRef
	}
	fonts?: BrandFontRef[]
	iconSet?: string
}

/** A DTCG leaf token as authored in `theme.json`. */
export interface ThemeToken {
	$type: TokenType
	$value: string
	$description?: string
}

/** A DTCG group: nested groups or tokens, plus an optional `$description`. */
export interface ThemeGroup {
	[name: string]: ThemeGroup | ThemeToken | string
}

/** How the brand looks: primitive → semantic → component token layers (R-THEME-001). */
export interface ThemeDefinition {
	$description?: string
	primitives: ThemeGroup
	semantic: ThemeGroup
	components?: ThemeGroup
}

/** How the brand is assembled: one registered ID per slot, per group. */
export interface DesignConfiguration {
	components: Record<string, string>
	patterns: Record<string, string>
	layouts: Record<string, string>
}

/** The normalized brand definition (tech spec §6). */
export interface BrandDefinition {
	schemaVersion: number
	id: string
	status: BrandStatus
	/** Absent means {@link BrandRole.Standard}; never defaulted in, so undeclared brands stay byte-identical. */
	role?: BrandRole
	identity: BrandIdentity
	theme: ThemeDefinition
	design: DesignConfiguration
}

/** The allowed design IDs per slot, per group. S3 (TO-368) fills the default registry. */
export interface DesignRegistry {
	components: Record<string, readonly string[]>
	patterns: Record<string, readonly string[]>
	layouts: Record<string, readonly string[]>
}

/** The three human-authored source files of a brand. */
export type BrandSourceFile = 'brand.json' | 'theme.json' | 'design.json'

/** A brand's source as read from `brands/<id>/`: file texts plus asset bytes keyed by `assets/…` path. */
export interface BrandSource {
	/** The source directory name. */
	id: string
	files: Partial<Record<BrandSourceFile, string>>
	assets: ReadonlyMap<string, Uint8Array>
}

/** The compiled artifact written to `dist/brands/<id>/`. */
export interface BrandArtifact {
	id: string
	/** Canonical normalized definition. */
	brandJson: string
	/** CSS custom properties of the semantic and component layers under `[data-brand="<id>"]`. */
	themeCss: string
	/** Flat map of every token's resolved value. */
	tokensJson: string
	/** Referenced asset files with roles, MIME type, size and SHA-256. */
	assetManifestJson: string
	/** Referenced asset bytes keyed by `assets/…` path. */
	assets: ReadonlyMap<string, Uint8Array>
}
